import { Page, PaginationButtonsProps, PaginationProps, SelectOption, SortDirection } from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
// import { RootStore } from 'mobx-stores'
import { ProductsDTO } from 'models'
import { CategoriesSortBy, Department, DepartmentCategory, DepartmentCategoryFilters, filterRatingCategories } from 'models/departments'
import { Api, FetchOptions } from 'services'
import _ from 'lodash';
import { getSelectOptions } from 'utilities'

export class DepartmentsStore {
  private rootStore: any
  private departmentsApi: Api['departmentsApi']

  constructor (rootStore: any) {
    this.rootStore = rootStore
    this.departmentsApi = this.rootStore.generalStore.api.departmentsApi;
    makeAutoObservable(this)
  }

  //OBSERVABLES
  private departments: Department[] = [];
  private categories: DepartmentCategory[] = [];
  private departmentCategoryProducts: Page<Partial<ProductsDTO>> = null;
  private categoryFilters: DepartmentCategoryFilters = {
    categories: [],
    stores: [],
    manufacturers: [],
    subcategories: [],
    types: [],
    priceMin: null,
    priceMax: null,
    ratingMin: null, 
    ratingMax: null,
    selectedPriceMin: null,
    selectedPriceMax: null
  };
  private ratingFilter = {
    rating: 0,
    direction: filterRatingCategories.UP
  }
  private categoryId: number = 0;
  private timeoutPriceSelect: NodeJS.Timeout;
  private timeoutRatingSelect: NodeJS.Timeout;
  public selectedCategorySorting: CategoriesSortBy = CategoriesSortBy.BESTSELLING
  public pagination: Partial<PaginationProps<any>> = {
    page: 0,
    size: 16,
    sortDirection: SortDirection.DESC,
    sortProp: 'sold',
    totalElements: 0,
    totalPages: 0
  };  

  // ACTIONS
  public setPaginationProps = (props: Partial<PaginationProps<any>>) => {
    this.pagination = {
    ...this.pagination,
    ...props
    }
  }

  public onPageSelect = (pageNumber: number) => {
    this.setPaginationProps({ page: pageNumber })
    this.getFilteredProducts();
  }

  public getPageQueryParams = (
    resetPageNumber: boolean
  ): Partial<FetchOptions['pageQueryParams']> => {
    return {
      page: resetPageNumber ? 0 : this.pagination.page,
      size: this.pagination.size,
      sortDirection: this.pagination.sortDirection,
      sortProp: this.pagination.sortProp
    }
  }

  public clearAllCategoryFilters = (): void => {
    let categoryFilter = _.cloneDeep(this.categoryFilters);
    this.categoryFilters = {
      categories: categoryFilter?.categories,
      stores: [],
      manufacturers: [],
      subcategories: [],
      types: [],
      ratingMin: null, 
      ratingMax: null,
      priceMin: categoryFilter?.priceMin,
      priceMax: categoryFilter?.priceMax,
      selectedPriceMin: categoryFilter?.priceMin,
      selectedPriceMax: categoryFilter?.priceMax
    };
    this.ratingFilter = {
      rating: 0,
      direction: filterRatingCategories.UP
    }
    this.getFilteredProducts();
    this.getCategoryFiltersById(this.categoryId);
  }

  public setCategoryId = (categoryId: number) => {
    this.categoryId = categoryId;
    this.categoryFilters.categories = [{id: categoryId}];
  }

  public handleCheckboxClickSubcategory = (expandId: number): void => {
    this.categoryFilters.subcategories[expandId].checked = !this.categoryFilters.subcategories[expandId].checked;
    this.getFilteredProducts();
  }

  public handleCheckboxClickStore = (expandId: number): void => {
    this.categoryFilters.stores[expandId].checked = !this.categoryFilters.stores[expandId].checked;
    this.getFilteredProducts();
  }

  public handleCheckboxClickManufacturer = (expandId: number): void => {
    this.categoryFilters.manufacturers[expandId].checked = !this.categoryFilters.manufacturers[expandId].checked;
    this.getFilteredProducts();
  }

  public handleExpandTypes = (expandId: number): void => {
    this.categoryFilters.subcategories[expandId].expanded = !this.categoryFilters.subcategories[expandId].expanded;
  }

  public handleCheckboxClickTypes = (expandId: number, expandTypeId: number): void => {
    this.categoryFilters.subcategories[expandId].types[expandTypeId].checked = !this.categoryFilters.subcategories[expandId].types[expandTypeId].checked;
    this.getFilteredProducts();
  }

  public setCategorySorting = (sort: CategoriesSortBy): void => {
    this.selectedCategorySorting = sort
    if(sort === CategoriesSortBy.TOPRATED) {
        this.setPaginationProps({ sortProp: 'rating' })
    } else {
        this.setPaginationProps({ sortProp: 'sold' })
    }
    this.getFilteredProducts();
  }  

  public removeFiterStores = (storeId): void => {
    const indexSelectedCheckAttr = this.categoryFilters?.stores?.findIndex(store => store?.id === storeId)
    this.categoryFilters.stores[indexSelectedCheckAttr].checked = false;
    this.getFilteredProducts();
  }

  public setRatingFilterRating = (rating: number) => {
    this.ratingFilter.rating = rating;
    this.filterByRating();
  }

  public setRatingFilterDirection = (direction) => {
    this.ratingFilter.direction = direction;
    this.filterByRating();
  }

  public getFilteredProducts = (): void => {
    let filterProducts = _.cloneDeep(this.categoryFilters);
    let typesSelected = [];
    for (const prop in filterProducts) {
      if(!!filterProducts[prop]?.length) {
        filterProducts[prop] = filterProducts[prop]?.filter(filter => filter?.checked);
      }
    }
    filterProducts.categories = [{id: this.categoryId}];
    this.categoryFilters?.subcategories?.forEach(subcategory => {
      subcategory?.types?.forEach(type => {
        if (type?.checked) {
          typesSelected.push(type)
        }
      })
    })
    filterProducts.types = typesSelected;
    filterProducts.priceMin = filterProducts?.selectedPriceMin ? filterProducts?.selectedPriceMin : filterProducts?.priceMin;
    filterProducts.priceMax = filterProducts?.selectedPriceMax ? filterProducts?.selectedPriceMax : filterProducts?.priceMax;
    this.getProductsByDepartmentCategoryId(filterProducts, this.categoryId);
  }

  public filterByRating = (): void => {
    if(this.ratingFilter.direction === filterRatingCategories.UP) {
      this.categoryFilters.ratingMin = !this.ratingFilter.rating ? null : this.ratingFilter.rating;
      this.categoryFilters.ratingMax = null;
    } else {
      this.categoryFilters.ratingMin = null;
      this.categoryFilters.ratingMax = this.ratingFilter.rating;
    }
    if (this.timeoutRatingSelect) {
      clearTimeout(this.timeoutRatingSelect)
    }
    this.timeoutRatingSelect = setTimeout(
      () => this.getFilteredProducts(),
      800
    )
  }

  public changePrice = (price: number[]): void => {
    this.categoryFilters.selectedPriceMin = price[0];
    this.categoryFilters.selectedPriceMax = price[1];
    if (this.timeoutPriceSelect) {
      clearTimeout(this.timeoutPriceSelect)
    }
    this.timeoutPriceSelect = setTimeout(
      () => this.getFilteredProducts(),
      800
    )
  }
 
  // COMPUTED
  public get allDepartments (): Department[] {
    return this.departments;
  }

  public get allCategories (): DepartmentCategory[] {
    return this.categories;
  }

  public get pageProductCategory (): Page<Partial<ProductsDTO>> { 
    return this.departmentCategoryProducts;
  }

  public get getCategoryFilters (): DepartmentCategoryFilters {
    return this.categoryFilters;
  }

  public get CategotySortBy (): SelectOption<CategoriesSortBy>[] {
    return getSelectOptions(CategoriesSortBy)
  }

  public get selectedCategorySortingSelected (): CategoriesSortBy {
    return this.selectedCategorySorting
  }

  public get categoryStoresSelected () {
    return this.categoryFilters?.stores?.filter(store => store?.checked);
  }

  public get paginationInfo (): PaginationButtonsProps {
    return {
    elementsPerPage: this.pagination.size,
    selectedPageNumber: this.pagination.page,
    totalElements: this.pagination.totalElements,
    totalPages: this.pagination.totalPages
    }
  }

  public get infoRatingFilter () {
    return this.ratingFilter
  }

  // METHODS
  public getAllDepartments = async (): Promise<Department[]> => {
    const allDepartments = await this.departmentsApi.getAllDepartments();
    if (!allDepartments) return;
    this.departments = allDepartments;
  } 

  public getAllCategories = async (departmentId: number): Promise<DepartmentCategory[]> => {
    const allCategories = await this.departmentsApi.getDepartmentCategories(departmentId);
    if (!allCategories) return;
    this.categories = allCategories;
  }

  public getCategoryFiltersById = async (
    categoryId: number
  ): Promise<DepartmentCategoryFilters> => {
    const filters = await this.departmentsApi.getFiltersByCategory(categoryId);
    if(!filters) return;
    this.categoryFilters = filters;
    this.categoryFilters.selectedPriceMin = this.categoryFilters.priceMin;
    this.categoryFilters.selectedPriceMax = this.categoryFilters.priceMax;
  }

  private getProductsByDepartmentCategoryId = async (
    filter: DepartmentCategoryFilters, 
    options?: any
): Promise<Page<Partial<ProductsDTO>>> => {
    const products = await this.departmentsApi.getAllProductsByDepartmentCategory(
        filter,
        { pageQueryParams: this.getPageQueryParams(options?.resetPageNumber) }
    )
    if(!products) return;
    this.pagination.totalElements = products.totalElements;
    this.pagination.totalPages = products.totalPages;
    this.departmentCategoryProducts = products;
}

}
